import { getThemeRuntime } from './theme-core.js';

const PRODUCT_FORM_ADDED_EVENT = 'product-form:added';
const conversionTimers = new WeakMap();

const rootPath = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

const announce = (message, alert = false) => {
  const region = document.getElementById(alert ? 'ThemeAlertRegion' : 'ThemeLiveRegion');
  if (region) region.textContent = String(message || '').slice(0, 180);
};

const updateCartBadges = (itemCount) => {
  const count = Math.max(0, Number(itemCount) || 0);
  const root = document.querySelector('[data-cart-root]');
  if (root) root.dataset.itemCount = String(count);
  for (const node of document.querySelectorAll('[data-cart-count]')) {
    node.textContent = String(count);
  }
  for (const bubble of document.querySelectorAll('[data-cart-count-bubble]')) {
    bubble.hidden = count === 0;
  }
  for (const trigger of document.querySelectorAll('[data-cart-drawer-trigger]')) {
    const label = count === 1
      ? String(trigger.dataset?.cartLabelOne || 'Open cart, 1 item')
      : String(trigger.dataset?.cartLabelOther || 'Open cart, __COUNT__ items').replace('__COUNT__', String(count));
    trigger.setAttribute('aria-label', label);
  }
};

const applyDrawerContent = (sections, minimumCount = 0) => {
  const html = sections?.['cart-drawer'] || sections?.cart_drawer;
  if (!html || typeof DOMParser !== 'function') return false;
  const parsed = new DOMParser().parseFromString(String(html), 'text/html');
  const next = parsed.querySelector('[data-cart-drawer-content]');
  const current = document.querySelector('[data-cart-drawer-content]');
  const root = document.querySelector('[data-cart-root]');
  if (!next || !current || !root) return false;
  current.replaceWith(document.importNode(next, true));
  const sectionCount = Number(next.dataset?.itemCount);
  const count = Math.max(
    Number.isFinite(sectionCount) ? sectionCount : 0,
    Math.max(0, Number(minimumCount) || 0),
  );
  updateCartBadges(count);
  const countLabel = root.querySelector('[data-cart-drawer-count]');
  if (countLabel) {
    countLabel.textContent = count === 1
      ? String(root.dataset?.itemCountOne || '1 item')
      : String(root.dataset?.itemCountOther || '__COUNT__ items').replace('__COUNT__', String(count));
  }
  return true;
};

const emitProductAdded = (detail) => {
  try {
    const runtime = getThemeRuntime();
    return runtime.bus.emit(PRODUCT_FORM_ADDED_EVENT, detail, { cancelable: true });
  } catch {
    return true;
  }
};

const addWithSections = async (variantIds) => {
  const sectionsUrl = `${window.location.pathname}${window.location.search}`;
  const body = variantIds.length === 1
    ? {
        id: variantIds[0],
        quantity: 1,
        sections: ['cart-drawer'],
        sections_url: sectionsUrl.startsWith('/') ? sectionsUrl : '/',
      }
    : {
        items: variantIds.map((id) => ({ id, quantity: 1 })),
        sections: ['cart-drawer'],
        sections_url: sectionsUrl.startsWith('/') ? sectionsUrl : '/',
      };

  const response = await fetch(`${rootPath()}cart/add.js`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let payload = null;
  try { payload = await response.json(); } catch { payload = null; }
  if (!response.ok) {
    throw new Error(String(payload?.description || 'Unable to add this item.').slice(0, 240));
  }
  return payload;
};

const refreshCartAfterAdd = async (quantity) => {
  const current = Number.parseInt(document.querySelector('[data-cart-root]')?.dataset?.itemCount || '0', 10) || 0;
  const expected = current + Math.max(1, Number(quantity) || 1);
  updateCartBadges(expected);

  const url = new URL(window.location.href);
  url.searchParams.delete('section_id');
  url.searchParams.set('sections', 'cart-drawer');
  url.searchParams.set('_cart_refresh', String(Date.now()));
  const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
  const sections = await response.json();
  applyDrawerContent(sections, expected);
  const dialog = document.querySelector('[data-cart-drawer-dialog]');
  if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
    try { dialog.showModal(); } catch { /* ignore */ }
  }
  document.dispatchEvent(new CustomEvent('cart:updated', {
    detail: { source: 'phase-4-quick-add', itemCount: expected },
  }));
};

const clearConversionTimer = (node) => {
  const timer = conversionTimers.get(node);
  if (timer) window.clearTimeout(timer);
  conversionTimers.delete(node);
};

const showTextButtonState = (button, state, label, restoreLabel = '', duration = 1100) => {
  if (!button) return;
  clearConversionTimer(button);
  button.dataset.atcState = state;
  if (label) button.textContent = label;
  if (state !== 'loading') button.removeAttribute('aria-busy');
  if (state === 'added' && restoreLabel && duration > 0) {
    const timer = window.setTimeout(() => {
      if (!button.isConnected) return;
      button.dataset.atcState = 'idle';
      button.textContent = restoreLabel;
      conversionTimers.delete(button);
    }, duration);
    conversionTimers.set(button, timer);
  }
};

const showProductCardAddedState = (statusNode) => {
  const card = statusNode?.closest?.('[data-product-card]');
  if (!card) return;
  const button = card.querySelector('[data-product-card-atc]');
  const labelNode = card.querySelector('[data-product-card-atc-label]');
  if (!button || !labelNode) return;

  const statusText = String(statusNode.textContent || '').trim().toLowerCase();
  if (!statusText.includes('added')) return;

  const addedLabel = String(card.dataset.atcAddedLabel || 'Added').trim();
  const idleLabel = String(card.dataset.atcLabel || 'Add to cart').trim();
  clearConversionTimer(button);
  button.dataset.atcState = 'added';
  labelNode.textContent = `✓ ${addedLabel}`;

  const timer = window.setTimeout(() => {
    if (!button.isConnected || !labelNode.isConnected) return;
    button.dataset.atcState = 'idle';
    labelNode.textContent = idleLabel;
    conversionTimers.delete(button);
  }, 1100);
  conversionTimers.set(button, timer);
};

const installProductCardFeedback = (root = document) => {
  for (const status of root.querySelectorAll?.('[data-product-card-atc-status]') || []) {
    if (status.dataset.conversionObserver === 'true') continue;
    status.dataset.conversionObserver = 'true';
    const observer = new MutationObserver(() => showProductCardAddedState(status));
    observer.observe(status, { childList: true, characterData: true, subtree: true });
  }
};

installProductCardFeedback(document);

const dynamicCardObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.('[data-product-card-atc-status]')) {
        installProductCardFeedback(node.parentElement || document);
      } else if (node.querySelector?.('[data-product-card-atc-status]')) {
        installProductCardFeedback(node);
      }
    }
  }
});

dynamicCardObserver.observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('click', async (event) => {
  const fbt = event.target?.closest?.('[data-fbt-add-all]');
  if (fbt) {
    event.preventDefault();
    const ids = String(fbt.dataset.fbtVariantIds || '')
      .split(',')
      .map((value) => Number.parseInt(value, 10))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (!ids.length) return;

    const originalLabel = String(fbt.textContent || 'Add all').trim();
    let successful = false;
    fbt.disabled = true;
    fbt.setAttribute('aria-busy', 'true');
    fbt.dataset.atcState = 'loading';
    try {
      const payload = await addWithSections(ids);
      const useFallback = emitProductAdded({
        variantId: String(ids[0]),
        quantity: ids.length,
        sellingPlanId: '',
        source: 'fbt-bundle',
        sections: payload?.sections ?? null,
      });
      successful = true;
      if (useFallback) {
        applyDrawerContent(payload?.sections, ids.length);
        window.location.href = `${rootPath()}cart`;
      } else {
        fbt.disabled = false;
        showTextButtonState(fbt, 'added', '✓ Added', originalLabel);
        announce('Bundle added to cart.');
      }
    } catch (error) {
      announce(error?.message || 'Could not add the bundle. Please try again.', true);
    } finally {
      if (!successful && fbt.isConnected) {
        fbt.disabled = false;
        fbt.removeAttribute('aria-busy');
        fbt.dataset.atcState = 'idle';
        fbt.textContent = originalLabel;
      }
    }
    return;
  }

  const quickAdd = event.target?.closest?.('[data-cart-quick-add]');
  if (!quickAdd) return;
  event.preventDefault();
  event.stopPropagation();
  const variantId = Number.parseInt(quickAdd.dataset.variantId || '', 10);
  if (!Number.isFinite(variantId) || variantId <= 0) return;
  const label = String(quickAdd.textContent || 'Add').trim();
  quickAdd.disabled = true;
  quickAdd.setAttribute('aria-busy', 'true');
  quickAdd.dataset.atcState = 'loading';
  quickAdd.textContent = 'Adding…';
  try {
    const payload = await addWithSections([variantId]);
    const useFallback = emitProductAdded({
      productId: '',
      variantId: String(variantId),
      quantity: 1,
      sellingPlanId: '',
      source: 'cart-quick-add',
      sections: payload?.sections ?? null,
    });
    if (useFallback) {
      await refreshCartAfterAdd(1);
    }
    if (quickAdd.isConnected) {
      quickAdd.disabled = false;
      showTextButtonState(quickAdd, 'added', '✓ Added', label);
    }
    announce(`${quickAdd.dataset.productTitle || 'Item'} added to cart.`);
  } catch (error) {
    announce(error?.message || 'Could not add that item. Please try again.', true);
    if (quickAdd.isConnected) {
      clearConversionTimer(quickAdd);
      quickAdd.textContent = label;
      quickAdd.disabled = false;
      quickAdd.removeAttribute('aria-busy');
      quickAdd.dataset.atcState = 'idle';
    }
  }
});
